/**
 * FlatBuffer Schema Tree — dynamic discovery via GitHub Tree API + WASM parser.
 *
 * Replaces the hardcoded CATEGORY_META approach with:
 *   1. GitHub Tree API call to discover ALL .fbs files in the repo
 *   2. Lazy-fetch individual files on tree node expand (not all 52 upfront)
 *   3. WASM-compiled nom parser for accurate AST extraction
 *   4. Two-tier caching: session cache for tree listing, Map for parsed files
 *
 * Tree structure:
 *   FlatBuffer Schemas
 *   ├── ln2/
 *   │   ├── usage.fbs          → (fetched + parsed on expand)
 *   │   │   ├── enum Usage : uint16 (163 variants)
 *   │   │   ├── table NormalUsage (1 field)
 *   │   │   └── union UsageVariant (2 members)
 *   │   ├── fault.fbs
 *   │   └── ...
 *   ├── config/
 *   ├── vendor/
 *   └── ...
 */

import { parseFbsWasm, WasmFbsFile, formatFbsType } from "./wasmParser";
import { fetchRepoTree, fetchGitHubFileContent } from "./SchemaGitService";

// ---------------------------------------------------------------------------
// Shared types (preserved from original API)
// ---------------------------------------------------------------------------

export type DeviceType = "Elevator" | "Escalator";

export interface SchemaEnumEntry {
  name: string;
  index: number;
}

/** A leaf node — an enum whose values can be viewed/extended */
export interface SchemaEnumNode {
  kind: "enum";
  label: string;
  fbsEnumName: string;
  fbsNamespace: string;
  fbsFile: string;
  fbsBackingType: string;
  deviceType: DeviceType;
  values: SchemaEnumEntry[];
}

/** An intermediate node that has children */
export interface SchemaBranchNode {
  kind: "branch";
  label: string;
  children: SchemaNode[];
}

/** A file node that hasn't been parsed yet */
export interface SchemaFileNode {
  kind: "file";
  label: string;
  /** Full path relative to repo root (e.g., "schemas/ln2/usage.fbs") */
  filePath: string;
  /** Whether children have been loaded */
  loaded: boolean;
  children: SchemaNode[];
}

/** A table node — fields can be viewed and new ones appended */
export interface SchemaTableNode {
  kind: "table";
  label: string;
  fbsTableName: string;
  fbsNamespace: string;
  fbsFile: string;
  fields: string[];
}

/** A read-only struct node */
export interface SchemaStructNode {
  kind: "struct";
  label: string;
  fields: string[];
}

/** A union node — members can be viewed and new ones appended */
export interface SchemaUnionNode {
  kind: "union";
  label: string;
  fbsUnionName: string;
  fbsNamespace: string;
  fbsFile: string;
  members: string[];
}

export type SchemaNode =
  | SchemaBranchNode
  | SchemaEnumNode
  | SchemaFileNode
  | SchemaTableNode
  | SchemaStructNode
  | SchemaUnionNode;

// ---------------------------------------------------------------------------
// Two-tier caching
// ---------------------------------------------------------------------------

/** Tier 1: Built tree skeleton (session-scoped, keyed by token) */
let treeCacheToken: string | null = null;
let treeCacheResult: SchemaBranchNode | null = null;

/** Tier 2: Parsed file results + raw content (keyed by file path) */
const parsedFileCache = new Map<string, WasmFbsFile>();
const rawContentCache = new Map<string, string>();

// ---------------------------------------------------------------------------
// Tree skeleton builder
// ---------------------------------------------------------------------------

/**
 * Fetches the repo tree and builds a directory skeleton without fetching
 * any file contents. Files appear as unexpanded nodes.
 */
export async function fetchSchemaTreeSkeleton(
  token: string,
  branch?: string,
): Promise<SchemaBranchNode> {
  const cacheKey = `${token}:${branch || "default"}`;

  // Return cached tree if same token+branch (guards against StrictMode double-mount)
  if (treeCacheResult && treeCacheToken === cacheKey) {
    return treeCacheResult;
  }

  // Token/branch changed — clear all caches
  if (treeCacheToken !== cacheKey) {
    parsedFileCache.clear();
    rawContentCache.clear();
  }

  const fbsFiles = await fetchRepoTree(token, branch);
  const tree = buildDirectoryTree(fbsFiles);
  treeCacheResult = tree;
  treeCacheToken = cacheKey;

  return tree;
}

/**
 * Groups file paths into a directory tree structure.
 * E.g., "schemas/ln2/usage.fbs" → { "schemas": { "ln2": ["usage.fbs"] } }
 */
function buildDirectoryTree(files: { path: string; sha: string }[]): SchemaBranchNode {
  // Group files by their directory
  const dirMap = new Map<string, { path: string; fileName: string }[]>();

  files.forEach(({ path }) => {
    const lastSlash = path.lastIndexOf("/");
    const dir = lastSlash >= 0 ? path.substring(0, lastSlash) : "";
    const fileName = lastSlash >= 0 ? path.substring(lastSlash + 1) : path;

    if (!dirMap.has(dir)) {
      dirMap.set(dir, []);
    }
    dirMap.get(dir)!.push({ path, fileName });
  });

  // Sort directories and build tree
  const sortedDirs = [...dirMap.keys()].sort();
  const children: SchemaNode[] = sortedDirs.map((dir) => {
    const dirFiles = dirMap.get(dir)!;
    const fileNodes: SchemaNode[] = dirFiles
      .sort((a, b) => a.fileName.localeCompare(b.fileName))
      .map((f) => ({
        kind: "file" as const,
        label: f.fileName,
        filePath: f.path,
        loaded: false,
        children: [],
      }));

    // Use the last segment of the directory path as label
    const dirLabel = dir.split("/").pop() || dir;

    return {
      kind: "branch" as const,
      label: dirLabel,
      children: fileNodes,
    };
  });

  return {
    kind: "branch",
    label: "FlatBuffer Schemas",
    children,
  };
}

// ---------------------------------------------------------------------------
// Lazy file fetch + parse
// ---------------------------------------------------------------------------

/**
 * Fetches and parses a single .fbs file. Results are cached in tier-2.
 * Returns the parsed AST.
 */
export async function fetchAndParseFile(
  filePath: string,
  token: string,
  branch?: string,
): Promise<WasmFbsFile> {
  // Check tier-2 cache
  const cached = parsedFileCache.get(filePath);
  if (cached) return cached;

  const { content } = await fetchGitHubFileContent(filePath, token, branch);
  const parsed = parseFbsWasm(content, filePath);

  // Cache both parsed result and raw content (raw is needed for PR diff preview)
  parsedFileCache.set(filePath, parsed);
  rawContentCache.set(filePath, content);

  return parsed;
}

/** Returns cached raw .fbs content if already fetched, or null */
export function getCachedRawContent(filePath: string): string | null {
  return rawContentCache.get(filePath) ?? null;
}

/**
 * Converts a parsed WasmFbsFile into SchemaNode children for display.
 * Enums become SchemaEnumNode leaves; tables/structs/unions are read-only.
 */
export function buildFileSubtree(parsed: WasmFbsFile, filePath: string): SchemaNode[] {
  const ns = parsed.namespace || "";
  const fbsFile = filePath.replace(/^schemas\//, "");
  const children: SchemaNode[] = [];

  // Enums → interactive enum nodes
  parsed.enums.forEach((e) => {
    const backingStr = formatFbsType(e.backing_type);
    let nextIndex = 0;
    const values: SchemaEnumEntry[] = e.variants.map((v) => {
      const idx =
        v.explicit_value !== null && v.explicit_value !== undefined ? v.explicit_value : nextIndex;
      nextIndex = idx + 1;
      return { name: v.name, index: idx };
    });

    // Create one node per device type for interoperability with existing add workflow
    children.push({
      kind: "enum" as const,
      label: `enum ${e.name} : ${backingStr} (${e.variants.length} variants)`,
      fbsEnumName: e.name,
      fbsNamespace: ns,
      fbsFile,
      fbsBackingType: backingStr,
      deviceType: "Elevator" as DeviceType,
      values,
    });
  });

  // Tables → editable (can add fields)
  parsed.tables.forEach((t) => {
    children.push({
      kind: "table" as const,
      label: `table ${t.name} (${t.fields.length} fields)`,
      fbsTableName: t.name,
      fbsNamespace: ns,
      fbsFile,
      fields: t.fields.map((f) => `${f.name}: ${formatFbsType(f.field_type)}`),
    });
  });

  // Structs → read-only display (FlatBuffers doesn't allow appending to structs)
  parsed.structs.forEach((s) => {
    children.push({
      kind: "struct" as const,
      label: `struct ${s.name} (${s.fields.length} fields)`,
      fields: s.fields.map((f) => `${f.name}: ${formatFbsType(f.field_type)}`),
    });
  });

  // Unions → editable (can add members)
  parsed.unions.forEach((u) => {
    children.push({
      kind: "union" as const,
      label: `union ${u.name} (${u.members.length} members)`,
      fbsUnionName: u.name,
      fbsNamespace: ns,
      fbsFile,
      members: u.members,
    });
  });

  return children;
}
